import { useState, useEffect, useCallback } from "react";
import { fetchPromotions } from "src/api/payload/promotions";
import { isPayloadPromotionsSource } from "src/utils/promotionsSource";

/**
 * Loads promotion hub cards from Payload CMS when REACT_APP_PROMOTIONS_SOURCE=payload.
 *
 * @param {string} locale - `en` | `fr`
 * @returns {{
 *   data: import('src/utils/mapPromotion').PromotionCard[],
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   isPayloadSource: boolean,
 * }}
 */
export function usePromotionHub(locale) {
  const isPayloadSource = isPayloadPromotionsSource();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(isPayloadSource);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!isPayloadSource) return;

    setLoading(true);
    setError(null);

    try {
      const cards = await fetchPromotions(locale);
      setData(cards);
    } catch (err) {
      console.error("Error fetching promotions from Payload:", err);
      setError(err?.message || "Failed to load promotions");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [locale, isPayloadSource]);

  useEffect(() => {
    if (!isPayloadSource) return;
    refetch();
  }, [isPayloadSource, refetch]);

  return {
    data,
    loading,
    error,
    refetch,
    isPayloadSource,
  };
}
